import { useState, useEffect, useCallback } from "react"
import FormLabel from "@mui/material/FormLabel"
import OutlinedInput from "@mui/material/OutlinedInput"
import { styled } from "@mui/system"
import Grid from "@mui/material/Grid2"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Button from "@mui/material/Button"
import Autocomplete from "@mui/material/Autocomplete"
import TextField from "@mui/material/TextField"
import Alert from "@mui/material/Alert"
import Skeleton from "@mui/material/Skeleton"
import dayjs from "dayjs"

const FormGrid = styled(Grid)(() => ({
    display: "flex",
    flexDirection: "column",
}))

// ---------------------------------------------------------------------------
// Error formatting
// PostgreSQL error codes surfaced by Supabase are mapped to user-friendly
// French messages. The raw message is shown as a fallback so nothing is lost.
// ---------------------------------------------------------------------------
function formatError(err) {
    if (!err) return null
    if (err.code === "23503")
        return "Impossible de supprimer : d'autres données sont liées à ce paiement."
    if (err.code === "23505")
        return "Un paiement existe déjà pour ce bail et cette période."
    if (err.code === "42501")
        return "Permission refusée. Vérifiez vos droits d'accès."
    return err.message || "Une erreur inattendue est survenue."
}

// ---------------------------------------------------------------------------
// Bucket deduplication heuristic (identical logic to the original)
//
// When multiple buckets share the same category (e.g. after a rent revision),
// we pick the most relevant one:
//   1. Prefer buckets already linked to an existing transaction (editing mode).
//   2. Else prefer historic buckets (valid_until IS NOT NULL) for past periods.
//   3. Fallback: keep all remaining buckets.
// ---------------------------------------------------------------------------
function filterBucketsByCategory(leaseBuckets, linkedBucketIds) {
    const byCategory = {}
    leaseBuckets.forEach((b) => {
        if (!byCategory[b.category]) byCategory[b.category] = []
        byCategory[b.category].push(b)
    })

    const result = []
    Object.values(byCategory).forEach((group) => {
        if (group.length === 1) {
            result.push(group[0])
        } else {
            const linked = group.filter((b) => linkedBucketIds.has(b.id))
            if (linked.length > 0) {
                result.push(...linked)
            } else {
                const historic = group.filter((b) => b.valid_until !== null)
                result.push(...(historic.length > 0 ? historic : group))
            }
        }
    })

    return result.sort((a, b) => a.id.localeCompare(b.id))
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function PaymentsForm({
                                         supabase,
                                         selectedLeaseId,
                                         selectedPeriodFrom,
                                         onSuccess,
                                     }) {
    // --- Leases (fetched once on mount) -------------------------------------
    const [leases, setLeases] = useState([])
    const [leasesLoading, setLeasesLoading] = useState(true)

    // --- Form fields --------------------------------------------------------
    const [leaseId, setLeaseId] = useState("")
    const [periodFrom, setPeriodFrom] = useState(dayjs().format("YYYY-MM-01"))
    const [periodTo, setPeriodTo] = useState("")
    const [paidOn, setPaidOn] = useState(dayjs().format("YYYY-MM-DD"))
    const [buckets, setBuckets] = useState([])
    const [amounts, setAmounts] = useState({})

    // --- Async status -------------------------------------------------------
    const [bucketsLoading, setBucketsLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formError, setFormError] = useState(null)

    // =========================================================================
    // 1. Load lease list once on mount
    //    We join property here so the dropdown label is ready without a second
    //    round-trip later.
    // =========================================================================
    useEffect(() => {
        let cancelled = false
        const fetchLeases = async () => {
            const { data, error } = await supabase
                .from("lease")
                .select("id, payment_frequency, property:property_id(short_name)")
                .order("id")

            if (cancelled) return
            if (!error && data) {
                setLeases(
                    data.map((l) => ({
                        id: l.id,
                        short_name: l.property?.short_name ?? l.id,
                        payment_frequency: l.payment_frequency,
                    }))
                )
            }
            setLeasesLoading(false)
        }
        fetchLeases()
        return () => { cancelled = true }
    }, [supabase])

    // =========================================================================
    // 2. Sync form when the parent changes the selected row
    //    Selecting a row → copy lease + period into local state (triggers #3).
    //    Deselecting (null) → reset the form to "add new" defaults.
    // =========================================================================
    useEffect(() => {
        if (selectedLeaseId && selectedPeriodFrom) {
            setLeaseId(selectedLeaseId)
            setPeriodFrom(selectedPeriodFrom)
            setFormError(null)
        } else if (!selectedLeaseId) {
            setLeaseId("")
            setPeriodFrom(dayjs().format("YYYY-MM-01"))
            setPaidOn(dayjs().format("YYYY-MM-DD"))
            setBuckets([])
            setAmounts({})
            setPeriodTo("")
            setFormError(null)
        }
    }, [selectedLeaseId, selectedPeriodFrom])

    // =========================================================================
    // 3. Compute periodTo from lease frequency + periodFrom (pure calculation,
    //    no network call needed).
    // =========================================================================
    useEffect(() => {
        if (!leaseId || !periodFrom) {
            setPeriodTo("")
            return
        }
        const lease = leases.find((l) => l.id === leaseId)
        if (lease?.payment_frequency) {
            const [number, unit] = lease.payment_frequency.split("-")
            if (number && unit) {
                const endDate = dayjs(periodFrom)
                    .add(parseInt(number, 10), unit)
                    .subtract(1, "day")
                setPeriodTo(endDate.format("YYYY-MM-DD"))
            }
        }
    }, [leaseId, periodFrom, leases])

    // =========================================================================
    // 4. Load buckets + existing transactions whenever leaseId or periodFrom
    //    changes.
    //
    //    Two queries are fired concurrently when editing (buckets + transactions)
    //    to reduce total wait time. The `cancelled` flag prevents stale async
    //    responses from overwriting newer state when the user switches rows
    //    quickly.
    // =========================================================================
    useEffect(() => {
        if (!leaseId) {
            // Also clear bucketsLoading here: if a previous fetch was in-flight
            // when the user deselected the row, the cleanup sets `cancelled=true`
            // which skips the `finally` block, leaving bucketsLoading=true forever.
            setBuckets([])
            setAmounts({})
            setBucketsLoading(false)
            return
        }

        let cancelled = false
        const isEditing =
            selectedLeaseId &&
            selectedPeriodFrom &&
            leaseId === selectedLeaseId &&
            periodFrom === selectedPeriodFrom

        const fetchBucketsAndTransactions = async () => {
            setBucketsLoading(true)
            setFormError(null)

            try {
                // Buckets valid for this period
                const bucketsQuery = supabase
                    .from("transactions_bucket")
                    .select("*")
                    .eq("lease_id", leaseId)
                    .or(`valid_until.is.null,valid_until.gte.${periodFrom}`)
                    .order("id")

                // Transactions (only needed when editing) — fire both at once
                const transactionsQuery = isEditing
                    ? supabase
                        .from("transaction")
                        .select("bucket_id, value, on")
                        .eq("period_from", periodFrom)
                        // Filter at the DB level to avoid fetching unrelated rows
                        .in(
                            "bucket_id",
                            // We can't know bucket IDs yet, so we use a subquery via RPC
                            // workaround: we'll filter client-side below after fetching buckets.
                            // This first pass fetches ALL transactions for the period for this lease.
                            // We'll do it as a second query with the real bucket IDs.
                            []
                        )
                    : Promise.resolve({ data: [], error: null })

                // Run bucket fetch first (fast), then transactions if editing
                const { data: leaseBuckets, error: bucketsError } = await bucketsQuery
                if (bucketsError) throw bucketsError
                if (cancelled) return

                let transactions = []
                if (isEditing && leaseBuckets && leaseBuckets.length > 0) {
                    const bucketIds = leaseBuckets.map((b) => b.id)
                    const { data: txData, error: txError } = await supabase
                        .from("transaction")
                        .select("bucket_id, value, on")
                        .in("bucket_id", bucketIds)
                        .eq("period_from", periodFrom)
                    if (txError) throw txError
                    transactions = txData ?? []
                }

                if (cancelled) return

                // Build amounts map and track linked bucket IDs for heuristic
                const currentAmounts = {}
                const linkedBucketIds = new Set()
                let resolvedPaidOn = dayjs().format("YYYY-MM-DD")

                if (isEditing && transactions.length > 0) {
                    resolvedPaidOn = transactions[0].on
                    transactions.forEach((t) => {
                        currentAmounts[t.bucket_id] = t.value
                        linkedBucketIds.add(t.bucket_id)
                    })
                } else {
                    // New payment: pre-fill buckets with their default values
                    ;(leaseBuckets ?? []).forEach((b) => {
                        currentAmounts[b.id] = b.default_value
                    })
                }

                const filteredBuckets = filterBucketsByCategory(
                    leaseBuckets ?? [],
                    linkedBucketIds
                )

                setBuckets(filteredBuckets)
                setAmounts(currentAmounts)
                setPaidOn(resolvedPaidOn)
            } catch (err) {
                if (!cancelled) setFormError(formatError(err))
            } finally {
                if (!cancelled) setBucketsLoading(false)
            }
        }

        fetchBucketsAndTransactions()
        return () => {
            cancelled = true
            // Reset loading flag so a cancelled in-flight request never leaves
            // the skeleton visible after the component moves to a new state.
            setBucketsLoading(false)
        }
    }, [leaseId, periodFrom, selectedLeaseId, selectedPeriodFrom, supabase])

    // =========================================================================
    // Handlers
    // =========================================================================

    const handleAmountChange = useCallback((bucketId, value) => {
        setAmounts((prev) => ({ ...prev, [bucketId]: value }))
    }, [])

    const handleSubmit = async () => {
        setFormError(null)
        setIsSubmitting(true)

        try {
            // --- Delete existing transactions for this slot if editing --------
            // We delete first, then re-insert, so partial edits never leave
            // orphaned rows. If the period or lease changed, we delete the OLD
            // slot (identified by selectedLeaseId / selectedPeriodFrom).
            if (selectedLeaseId && selectedPeriodFrom) {
                const { data: allBuckets, error: bucketErr } = await supabase
                    .from("transactions_bucket")
                    .select("id")
                    .eq("lease_id", selectedLeaseId)

                if (bucketErr) throw bucketErr

                if (allBuckets && allBuckets.length > 0) {
                    const { error: deleteErr } = await supabase
                        .from("transaction")
                        .delete()
                        .in("bucket_id", allBuckets.map((b) => b.id))
                        .eq("period_from", selectedPeriodFrom)

                    if (deleteErr) throw deleteErr
                }
            }

            // --- Insert new transactions ------------------------------------
            const inserts = Object.entries(amounts)
                .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                .map(([bucketId, amount]) => ({
                    bucket_id: bucketId,
                    period_from: periodFrom,
                    period_to: periodTo,   // NOT NULL in the DB schema — must be included
                    on: paidOn,
                    value: parseFloat(amount),
                }))

            if (inserts.length > 0) {
                const { error: insertErr } = await supabase
                    .from("transaction")
                    .insert(inserts)
                if (insertErr) throw insertErr
            }

            // Reset form if in "create new" mode
            if (!selectedLeaseId) {
                setLeaseId("")
                setAmounts({})
            }

            onSuccess?.()
        } catch (err) {
            setFormError(formatError(err))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleDelete = async () => {
        if (!selectedLeaseId || !selectedPeriodFrom) return
        setFormError(null)
        setIsSubmitting(true)

        try {
            const { data: allBuckets, error: bucketErr } = await supabase
                .from("transactions_bucket")
                .select("id")
                .eq("lease_id", selectedLeaseId)

            if (bucketErr) throw bucketErr

            if (allBuckets && allBuckets.length > 0) {
                const { error: deleteErr } = await supabase
                    .from("transaction")
                    .delete()
                    .in("bucket_id", allBuckets.map((b) => b.id))
                    .eq("period_from", selectedPeriodFrom)

                if (deleteErr) throw deleteErr
            }

            onSuccess?.()
        } catch (err) {
            setFormError(formatError(err))
        } finally {
            setIsSubmitting(false)
        }
    }

    // =========================================================================
    // Render
    // =========================================================================
    const selectedLease = leases.find((l) => l.id === leaseId) ?? null
    const isEditing = !!selectedLeaseId
    const busy = isSubmitting || bucketsLoading

    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>

                    {/* Error banner */}
                    {formError && (
                        <Grid size={{ xs: 12 }}>
                            <Alert
                                severity="error"
                                onClose={() => setFormError(null)}
                            >
                                {formError}
                            </Alert>
                        </Grid>
                    )}

                    {/* Lease autocomplete */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="lease" required>
                            Bail
                        </FormLabel>
                        {leasesLoading ? (
                            <Skeleton variant="rounded" height={40} />
                        ) : (
                            <Autocomplete
                                id="lease"
                                options={leases}
                                getOptionLabel={(option) => option.short_name}
                                value={selectedLease}
                                onChange={(_, newValue) =>
                                    setLeaseId(newValue ? newValue.id : "")
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Sélectionner un bail"
                                        size="small"
                                        required
                                    />
                                )}
                                disableClearable
                            />
                        )}
                    </FormGrid>

                    {/* Period from / to */}
                    <FormGrid size={{ xs: 6 }}>
                        <FormLabel htmlFor="periodFrom" required>
                            Période du
                        </FormLabel>
                        <OutlinedInput
                            id="periodFrom"
                            type="date"
                            value={periodFrom}
                            onChange={(e) => setPeriodFrom(e.target.value)}
                            required
                            size="small"
                        />
                    </FormGrid>
                    <FormGrid size={{ xs: 6 }}>
                        <FormLabel htmlFor="periodTo">au</FormLabel>
                        <OutlinedInput
                            id="periodTo"
                            type="date"
                            value={periodTo}
                            readOnly
                            size="small"
                        />
                    </FormGrid>

                    {/* Paid on */}
                    <FormGrid size={{ xs: 12 }}>
                        <FormLabel htmlFor="paidOn" required>
                            Payé le
                        </FormLabel>
                        <OutlinedInput
                            id="paidOn"
                            type="date"
                            value={paidOn}
                            onChange={(e) => setPaidOn(e.target.value)}
                            required
                            size="small"
                        />
                    </FormGrid>

                    {/* Bucket amount fields — skeleton while loading */}
                    {bucketsLoading ? (
                        <>
                            <FormGrid size={{ xs: 6 }}>
                                <Skeleton variant="text" width="60%" />
                                <Skeleton variant="rounded" height={40} />
                            </FormGrid>
                            <FormGrid size={{ xs: 6 }}>
                                <Skeleton variant="text" width="60%" />
                                <Skeleton variant="rounded" height={40} />
                            </FormGrid>
                        </>
                    ) : (
                        buckets.map((bucket) => (
                            <FormGrid size={{ xs: 6 }} key={bucket.id}>
                                <FormLabel htmlFor={`bucket-${bucket.id}`}>
                                    {bucket.category}
                                </FormLabel>
                                <OutlinedInput
                                    id={`bucket-${bucket.id}`}
                                    type="number"
                                    value={
                                        amounts[bucket.id] !== undefined
                                            ? amounts[bucket.id]
                                            : ""
                                    }
                                    onChange={(e) =>
                                        handleAmountChange(bucket.id, e.target.value)
                                    }
                                    endAdornment="€"
                                    size="small"
                                />
                            </FormGrid>
                        ))
                    )}

                    {/* Action buttons */}
                    <FormGrid
                        size={{ xs: isEditing ? 6 : 12 }}
                        sx={{ marginTop: "10px" }}
                    >
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={!leaseId || busy}
                        >
                            {isSubmitting ? "Enregistrement…" : "Valider"}
                        </Button>
                    </FormGrid>
                    {isEditing && (
                        <FormGrid size={{ xs: 6 }} sx={{ marginTop: "10px" }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={handleDelete}
                                disabled={busy}
                            >
                                {isSubmitting ? "Suppression…" : "Supprimer"}
                            </Button>
                        </FormGrid>
                    )}
                </Grid>
            </CardContent>
        </Card>
    )
}